import { useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, Eye, Archive, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { relative } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, ConfirmDialog, Field, Input, LoadingBlock, Modal,
  PageHeader, Select, Textarea, useToast,
} from '@/components/ui';
import type {
  EmailTemplate, EmailTemplateList, TemplateAudience, TemplatePreview,
} from '@shared/types';

const AUDIENCE_LABEL: Record<TemplateAudience, string> = {
  leads: 'Leads',
  customers: 'Customers',
  general: 'Either',
  call: 'Call scripts',
};

const AUDIENCE_HINT: Record<TemplateAudience, string> = {
  leads: 'Offered on the Leads tab. Can use a business name and a town.',
  customers: 'Offered on the Email tab. Can use an organisation and a plan.',
  general: 'Offered on both, so only the placeholders they share.',
  call: 'Read on screen while you are on the phone. No subject, nothing sent.',
};

/** A call script is read aloud, so it has no subject and no sending address. */
const IS_SCRIPT = (audience: TemplateAudience) => audience === 'call';

const EMPTY = {
  name: '',
  audience: 'leads' as TemplateAudience,
  subject: '',
  body: '',
  from_address: '',
  from_name: '',
  description: '',
};

/**
 * Email written once and sent many times.
 *
 * ── Why the sender is part of the template ────────────────────────────────
 *
 * Which address a message leaves as is a property of the message, not of the
 * person pressing send. Outreach should come from leads@; a note about
 * somebody's plan should come from no-reply@. Putting it on the template means
 * that decision is made once, by whoever wrote the words, rather than
 * remembered correctly on every send.
 *
 * The picker only offers addresses on a domain this deployment has
 * authenticated, because an address it has not is refused by the API — a
 * choice that cannot work does not belong in a list.
 */
export default function Templates() {
  const toast = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const { data, loading, reload } = useApi<EmailTemplateList>(
    '/admin/email/templates', { audience: 'all', archived: showArchived },
  );

  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [confirming, setConfirming] = useState<EmailTemplate | null>(null);

  const templates = useMemo(() => data?.templates ?? [], [data]);
  const variables = data?.variables?.[form.audience] ?? [];

  const grouped = useMemo(() => {
    const out: Record<string, EmailTemplate[]> = { leads: [], customers: [], general: [] };
    for (const t of templates) (out[t.audience] ??= []).push(t);
    return out;
  }, [templates]);

  function openNew() {
    setForm(EMPTY);
    setEditing(null);
    setPreview(null);
    setCreating(true);
  }

  function openEdit(template: EmailTemplate) {
    setForm({
      name: template.name,
      audience: template.audience,
      subject: template.subject,
      body: template.body,
      from_address: template.from_address ?? '',
      from_name: template.from_name ?? '',
      description: template.description ?? '',
    });
    setEditing(template);
    setPreview(null);
    setCreating(true);
  }

  async function save() {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/email/templates/${editing.id}`, form);
        toast.success('Saved');
      } else {
        await api.post('/admin/email/templates', form);
        toast.success('Template created');
      }
      setCreating(false);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be saved');
    } finally {
      setBusy(false);
    }
  }

  async function runPreview() {
    setBusy(true);
    try {
      setPreview(await api.post<TemplatePreview>('/admin/email/templates/preview', {
        audience: form.audience, subject: form.subject, body: form.body,
      }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be previewed');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Delete, which sometimes archives instead.
   *
   * A template nobody has sent is a draft and goes for good. One that has been
   * sent is part of the record — the email log keeps every rendered body, but
   * not which template produced it — so it is archived, and the API says so in
   * a sentence this shows verbatim rather than paraphrasing.
   */
  async function remove(template: EmailTemplate, purge: boolean) {
    try {
      const res = await api.del<{ deleted: boolean; archived: boolean; message?: string }>(
        `/admin/email/templates/${template.id}`, purge ? { purge: 'true' } : undefined,
      );
      toast.success(res.message ?? 'Deleted');
      setConfirming(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be deleted');
    }
  }

  async function restore(template: EmailTemplate) {
    try {
      await api.patch(`/admin/email/templates/${template.id}`, { is_archived: false });
      toast.success('Back in use');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be restored');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email templates"
        subtitle="Written once here, then offered on the Leads and Email tabs"
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={openNew}>
            New template
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted">
          Placeholders in {'{{braces}}'} are filled in per recipient. An unknown one is
          left in the message exactly as written, so a typo shows up in the preview
          rather than vanishing.
        </p>
        <Button
          variant="ghost"
          size="sm"
          icon={<Archive className="h-4 w-4" />}
          onClick={() => setShowArchived((v) => !v)}
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </div>

      {loading && !data ? <LoadingBlock rows={4} /> : (
        <div className="space-y-6">
          {(['leads', 'customers', 'general', 'call'] as TemplateAudience[]).map((audience) => (
            <Card key={audience}>
              <CardHeader
                title={AUDIENCE_LABEL[audience]}
                subtitle={AUDIENCE_HINT[audience]}
              />
              <div className="p-5 sm:p-6 pt-4">
                {grouped[audience].length === 0 ? (
                  <p className="text-sm text-faint">Nothing here yet.</p>
                ) : (
                  <ul className="divide-y divide-line -my-3">
                    {grouped[audience].map((template) => (
                      <li key={template.id} className="py-3 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-ink truncate">{template.name}</span>
                            {template.is_archived && <Badge tone="neutral">archived</Badge>}
                            {template.use_count > 0 && (
                              <Badge tone="info">sent {template.use_count}</Badge>
                            )}
                            {template.unknown_placeholders.length > 0 && (
                              <Badge tone="warn">
                                {template.unknown_placeholders.length} unknown placeholder
                                {template.unknown_placeholders.length === 1 ? '' : 's'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted truncate mt-0.5">{template.subject}</p>
                          <p className="text-xs text-faint mt-1">
                            {template.from_address
                              ? <>from {template.from_address}</>
                              : <>from the default address</>}
                            {template.last_used_at && <> · last sent {relative(template.last_used_at)}</>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {template.is_archived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<RotateCcw className="h-4 w-4" />}
                              onClick={() => void restore(template)}
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Pencil className="h-4 w-4" />}
                              onClick={() => openEdit(template)}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 className="h-4 w-4" />}
                            onClick={() => setConfirming(template)}
                          >
                            Delete
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={editing ? 'Edit template' : 'New template'}
        subtitle={AUDIENCE_HINT[form.audience]}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              variant="secondary"
              icon={<Eye className="h-4 w-4" />}
              loading={busy}
              onClick={() => void runPreview()}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!form.name || (!IS_SCRIPT(form.audience) && !form.subject) || !form.body}
              onClick={() => void save()}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" hint="What you will pick it by">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="First contact"
              />
            </Field>
            <Field label="Used on">
              <Select
                value={form.audience}
                onChange={(e) => setForm((f) => ({
                  ...f, audience: e.target.value as TemplateAudience,
                }))}
              >
                {(data?.audiences ?? []).map((a) => (
                  <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>
                ))}
              </Select>
            </Field>
          </div>

          {/*
            Hidden for a call script rather than disabled. A sending address
            and a subject on something nobody sends are two fields that can
            only be filled in wrongly.
          */}
          {!IS_SCRIPT(form.audience) && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="From"
              hint="Only addresses on a domain this deployment can send from"
            >
              <Select
                value={form.from_address}
                onChange={(e) => setForm((f) => ({ ...f, from_address: e.target.value }))}
              >
                <option value="">Default ({data?.default_sender})</option>
                {(data?.senders ?? []).map((address) => (
                  <option key={address} value={address}>{address}</option>
                ))}
              </Select>
            </Field>
            <Field label="Sender name" hint="What a recipient sees beside the address">
              <Input
                value={form.from_name}
                onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
                placeholder="PROVIA"
              />
            </Field>
          </div>
          )}

          {!IS_SCRIPT(form.audience) && (
            <Field label="Subject">
              <Input
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="A quicker way to run {{business_name}}"
              />
            </Field>
          )}

          <Field
            label={IS_SCRIPT(form.audience) ? 'Script' : 'Message'}
            hint={IS_SCRIPT(form.audience)
              ? 'Read on screen while you are on the phone. Nothing here is sent to anybody.'
              : 'Plain text. The unsubscribe line is added for you.'}
          >
            <Textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={12}
              className="min-h-[14rem] font-mono text-xs"
              placeholder={'Hello {{first_name}},\n\n…'}
            />
          </Field>

          {variables.length > 0 && (
            <div>
              <p className="text-xs text-faint mb-1.5">
                Click to insert. Anything else stays in the message as typed.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, body: `${f.body}{{${v}}}` }))}
                    className="text-xs px-2 py-1 rounded-lg bg-raised border border-line
                               text-muted hover:text-ink transition-colors font-mono"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {preview && (
            <div className="rounded-xl border border-line overflow-hidden">
              <div className="px-3 py-2 bg-raised border-b border-line text-xs text-faint">
                Against {preview.against}
              </div>
              <div className="p-3 space-y-2">
                <p className="text-sm font-medium text-ink">{preview.subject || '(no subject)'}</p>
                <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">
                  {preview.body || '(empty)'}
                </p>
              </div>
              {preview.unknown_placeholders.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                                border-t border-warn/25 px-3 py-2.5">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Nothing fills in{' '}
                    <span className="font-mono">
                      {preview.unknown_placeholders.map((p) => `{{${p}}}`).join(', ')}
                    </span>{' '}
                    for this audience, so it would arrive written out like that.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && void remove(confirming, confirming.use_count === 0)}
        title={confirming?.use_count ? 'Archive this template?' : 'Delete this template?'}
        message={confirming?.use_count
          ? `"${confirming.name}" has been sent ${confirming.use_count} times, so it will be `
            + 'archived rather than deleted — the log says what went out, but not which '
            + 'template wrote it. You can delete it for good afterwards.'
          : `"${confirming?.name}" has never been sent, so there is nothing to keep.`}
        confirmLabel={confirming?.use_count ? 'Archive' : 'Delete'}
        tone="danger"
      />
    </div>
  );
}
